import React, { useState, useEffect, useRef, useCallback } from 'react'
import { fetchCallToken } from './lib/api.js'
import { createVideoCallClient } from './lib/videoCallClient.js'
import { createWhiteboardClient } from './lib/whiteboardClient.js'
import { notifyReady, notifyLeave, notifyPeerLeft, notifyAuthExpired } from './lib/bridge.js'
import CallView from './components/CallView.jsx'
import Whiteboard from './components/Whiteboard.jsx'
import './styles.css'

export default function App({ bootstrap }) {
  const [phase, setPhase] = useState('loading') // loading | error | call
  const [error, setError] = useState(null)
  const [tokenInfo, setTokenInfo] = useState(null)

  const [callState, setCallState] = useState(null)
  const callClientRef = useRef(null)

  const [whiteboardOpen, setWhiteboardOpen] = useState(false)
  const [wbState, setWbState] = useState(null)
  const wbClientRef = useRef(null)

  // Leave confirmation — lifted here so both the on-screen leave
  // button (CallView) and the Android hardware back button
  // (forwarded from WebCallView.tsx via a custom DOM event) open
  // the exact same confirm modal.
  const [confirmingLeave, setConfirmingLeave] = useState(false)

  const leftRef = useRef(false)

  // ── 1. Validate bootstrap params, fetch call token (unless the
  // RN app already fetched one for us — see bootstrap.prefetchedCallInfo
  // doc in main.jsx) ──────────────────────────────────────────
  useEffect(() => {
    if (!bootstrap.taskId || !bootstrap.idToken || !bootstrap.apiBaseUrl) {
      setError('Missing required parameters (taskId, idToken, apiBaseUrl).')
      setPhase('error')
      return
    }

    // RN's VideoCallScreen already called this same endpoint
    // before opening this WebView — reusing that result instead
    // of fetching again avoids double-recording the call session,
    // double-firing the incoming-call push, and double-consuming
    // the pre-hire call-count limit for a single call attempt.
    if (bootstrap.prefetchedCallInfo) {
      const result = bootstrap.prefetchedCallInfo
      if (result.provider !== 'webrtc') {
        setError(`This page only supports provider "webrtc" (server returned "${result.provider}"). If your backend is on CALL_PROVIDER=livekit, this page isn't the right client — LiveKit's own web SDK would be used instead.`)
        setPhase('error')
        return
      }
      setTokenInfo(result)
      setPhase('call')
      notifyReady()
      return
    }

    let cancelled = false
    ;(async () => {
      try {
        const result = await fetchCallToken({
          apiBaseUrl:    bootstrap.apiBaseUrl,
          taskId:        bootstrap.taskId,
          callType:      bootstrap.callType,
          applicationId: bootstrap.applicationId,
          idToken:       bootstrap.idToken,
          appCheckToken: bootstrap.appCheckToken,
        })
        if (cancelled) return

        if (result.provider !== 'webrtc') {
          setError(`This page only supports provider "webrtc" (server returned "${result.provider}"). If your backend is on CALL_PROVIDER=livekit, this page isn't the right client — LiveKit's own web SDK would be used instead.`)
          setPhase('error')
          return
        }

        setTokenInfo(result)
        setPhase('call')
        notifyReady()
      } catch (err) {
        if (cancelled) return
        // Send error details to RN host for debugging
        if (typeof window !== 'undefined' && window.ReactNativeWebView) {
          window.ReactNativeWebView.postMessage(JSON.stringify({
            type: 'js-error',
            msg: `Token fetch failed: status=${err.status} message=${err.message} body=${JSON.stringify(err.body)}`,
            src: 'App.jsx', line: 0,
          }))
        }
        if (err.isAppCheck) {
          // An attestation hiccup is NOT an expired login: do not tell the
          // app the session died (that would end the call), and do not show
          // technical wording.
          setError(err.message)
        } else if (err.status === 401) {
          notifyAuthExpired()
          setError('Your session expired. Please rejoin.')
        } else {
          setError(err.message || 'Could not start the call.')
        }
        setPhase('error')
      }
    })()

    return () => { cancelled = true }
  }, [bootstrap])

  // ── 2. Start the video call client once we have a token ─────
  useEffect(() => {
    if (phase !== 'call' || !tokenInfo) return

    const client = createVideoCallClient({
      apiBaseUrl:    bootstrap.apiBaseUrl,
      wsPath:        tokenInfo.wsPath,
      iceServers:    tokenInfo.iceServers,
      taskId:        bootstrap.taskId,
      callType:      bootstrap.callType,
      applicationId: bootstrap.applicationId,
      idToken:       bootstrap.idToken,
      // FIX: sessionId was not forwarded here — the signaling server
      // stores it from the auth message and calls recordCallEnd on
      // disconnect. Without it every call left its CallSession row
      // open permanently, causing hasActiveCallSession to suppress
      // the incoming-call notification on every subsequent attempt.
      sessionId:     tokenInfo.sessionId || undefined,
      voiceOnly:     bootstrap.callType === 'voice',
      onState: (s) => setCallState({
        ...s,
        toggleMic:         callClientRef.current?.toggleMic,
        toggleCamera:      callClientRef.current?.toggleCamera,
        startScreenShare:  callClientRef.current?.startScreenShare,
        stopScreenShare:   callClientRef.current?.stopScreenShare,
        notifyWhiteboardOpen: callClientRef.current?.notifyWhiteboardOpen,
      }),
    })
    callClientRef.current = client

    return () => { client.leave() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, tokenInfo])

  // ── 5. Leave — single path, notifies host, closes clients ───
  // reason: 'local' = user pressed hang-up, 'remote' = peer disconnected.
  // Uses different bridge message so WebCallView can bypass hasLeftRef
  // guard for remote disconnects (the guard exists to prevent double-
  // navigation on local leave, not to block remote peer-left).
  const handleLeave = useCallback((reason = 'local') => {
    if (leftRef.current) return
    leftRef.current = true
    wbClientRef.current?.close()
    callClientRef.current?.leave()
    if (reason === 'remote') notifyPeerLeft()
    else notifyLeave()
  }, [])

  // ── 3. Auto-leave once connected and later disconnected ─────
  // Terminal state guard: once leftRef is true (handleLeave ran),
  // ignore all subsequent connectionState changes — they are
  // artifacts of the WS/PC teardown sequence, not new call events.
  const hasConnectedRef = useRef(false)
  useEffect(() => {
    if (!callState) return
    if (leftRef.current) return
    if (callState.connectionState === 'connected') hasConnectedRef.current = true
    if (callState.connectionState === 'failed') handleLeave('remote')
    if (callState.connectionState === 'disconnected' && hasConnectedRef.current) handleLeave('remote')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callState?.connectionState])

  // ── 3b. Peer opened the whiteboard — auto-open ours too ──────
  // FIX: use a ref to check whiteboardOpen — the effect only
  // re-registers when peerOpenedWhiteboard changes, so `whiteboardOpen`
  // in the closure would be stale if it changed between the last
  // peerOpenedWhiteboard transition and now. A ref is always current.
  // Also: use openWhiteboardSilent (no echo back to the peer) —
  // previously calling openWhiteboard() here sent whiteboard-open
  // back to the peer who just sent it, which was unnecessary and
  // could cause the peer's whiteboard to re-open if they had
  // minimized it right before we echoed.
  const whiteboardOpenRef = useRef(whiteboardOpen)
  useEffect(() => { whiteboardOpenRef.current = whiteboardOpen }, [whiteboardOpen])

  useEffect(() => {
    if (callState?.peerOpenedWhiteboard && !whiteboardOpenRef.current) {
      openWhiteboardSilent()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callState?.peerOpenedWhiteboard])

  // ── 3c. RN hardware back button forwarded from WebCallView.tsx ─
  useEffect(() => {
    const handler = () => setConfirmingLeave(true)
    window.addEventListener('rn-back-pressed', handler)
    return () => window.removeEventListener('rn-back-pressed', handler)
  }, [])

  // ── 3d. Caller cancelled before we answered ─────────────────
  // Fired by WebCallView.tsx's notifyCallCancelled() → injectJavaScript
  // → window.dispatchEvent('rn-call-cancelled'). The caller hung up
  // before the WS room connected — leave immediately without showing
  // the confirm modal (there's nobody left to disconnect).
  useEffect(() => {
    const handler = () => {
      console.log('[App] rn-call-cancelled — leaving immediately')
      handleLeave()
    }
    window.addEventListener('rn-call-cancelled', handler)
    return () => window.removeEventListener('rn-call-cancelled', handler)
  }, [handleLeave])

  // ── 3e. RN navigated away before WebView teardown ────────────
  // Fired by WebCallView.forceLeave() → injectJavaScript when
  // the screen unmounts. Tears down WS and PC immediately so the
  // WebView doesn't keep sending offers after RN is gone.
  useEffect(() => {
    const handler = () => {
      console.log('[App] rn-force-leave — tearing down immediately')
      handleLeave('remote')
    }
    window.addEventListener('rn-force-leave', handler)
    return () => window.removeEventListener('rn-force-leave', handler)
  }, [handleLeave])


  // ── 4. Whiteboard — lazy, mounted once opened, stays mounted ─
  const [hasOpenedWhiteboard, setHasOpenedWhiteboard] = useState(false)
  useEffect(() => {
    if (!hasOpenedWhiteboard || !tokenInfo) return
    const client = createWhiteboardClient({
      apiBaseUrl:    bootstrap.apiBaseUrl,
      taskId:        bootstrap.taskId,
      applicationId: bootstrap.applicationId,
      idToken:       bootstrap.idToken,
          onState: (s) => setWbState({
        ...s, client,
        apiBaseUrl: bootstrap.apiBaseUrl,
        taskId: bootstrap.taskId,
        idToken: bootstrap.idToken,
        appCheckToken: bootstrap.appCheckToken,
        applicationId: bootstrap.applicationId,
        taskTitle: bootstrap.taskTitle,
        // Prefer the URL param (set by the RN app from nav params),
        // fall back to otherPartyName from the token response which
        // the backend now always includes — so the PDF header always
        // shows the other party's name regardless of who initiated.
        otherPartyName: bootstrap.otherPartyName ?? tokenInfo?.otherPartyName ?? null,
      }),
    })
    wbClientRef.current = client
    return () => client.close()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasOpenedWhiteboard, tokenInfo])

  // FIX: split into two variants —
  //   openWhiteboard()       — called when the LOCAL user taps the
  //                            whiteboard button; notifies the peer.
  //   openWhiteboardSilent() — called when the PEER's signal triggers
  //                            auto-open; does NOT echo back, avoiding
  //                            an unnecessary round-trip and the edge
  //                            case where the echo would re-open the
  //                            peer's whiteboard if they had just
  //                            minimized it.
  const openWhiteboardSilent = () => {
    setHasOpenedWhiteboard(true)
    setWhiteboardOpen(true)
  }
  const openWhiteboard = () => {
    openWhiteboardSilent()
    callClientRef.current?.notifyWhiteboardOpen?.()
  }
  const closeWhiteboard = () => setWhiteboardOpen(false) // container stays mounted — see WhiteboardCanvas crash history in RN app for why this matters even less here (browser <canvas> has no equivalent lifecycle hazard), kept for parity/simplicity anyway

  // ── Session timer inputs — null when the category has no
  // duration cap (e.g. pre-hire calls, which only have a
  // call-count cap, not a duration cap — see checkPreHireCallAllowed
  // server-side). CallView renders no timer when either is null.
  const timeLimitSeconds = tokenInfo?.callTimeLimitMinutes ? tokenInfo.callTimeLimitMinutes * 60 : null
  const callStartedAtMs  = tokenInfo?.callStartedAt ? new Date(tokenInfo.callStartedAt).getTime() : null

  if (phase === 'loading') {
    return <div className="center-screen"><div className="spinner" /><span>Connecting...</span></div>
  }

  if (phase === 'error') {
    return (
      <div className="center-screen">
        <h3>Couldn't join</h3>
        <p>{error}</p>
      </div>
    )
  }

  if (!callState) {
    return <div className="center-screen"><div className="spinner" /><span>Connecting...</span></div>
  }

  return (
    <>
      <CallView
        call={callState}
        voiceOnly={bootstrap.callType === 'voice'}
        taskTitle={bootstrap.taskTitle}
        whiteboardOpen={whiteboardOpen}
        onToggleWhiteboard={openWhiteboard}
        onLeave={handleLeave}
        confirmingLeave={confirmingLeave}
        onRequestLeaveConfirm={() => setConfirmingLeave(true)}
        onCancelLeaveConfirm={() => setConfirmingLeave(false)}
        timeLimitSeconds={timeLimitSeconds}
        callStartedAtMs={callStartedAtMs}
      />
      {hasOpenedWhiteboard && wbState && whiteboardOpen && (
        <Whiteboard
          wb={wbState}
          call={callState}
          onMinimize={closeWhiteboard}
          timeLimitSeconds={timeLimitSeconds}
          callStartedAtMs={callStartedAtMs}
        />
      )}
      {hasOpenedWhiteboard && !whiteboardOpen && (
        <button className="wb-minimized-bubble" onClick={() => setWhiteboardOpen(true)}>
          🖊️
        </button>
      )}
    </>
  )
}